"use client"

import { useMemo, useState } from "react"
import { FormikProvider, type FormikErrors, useFormik } from "formik"

import { FormInput } from "@/components/form-inputs"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useCreateMetaAdsFormCopyMutation } from "@/hooks/mutations/meta-ads"
import type { MetaAdsFormCopyResult, MetaLeadForm } from "@/lib/meta-ads/types"

type FormCopyValues = {
  name: string
}

export function MetaAdsFormCopyDialog({
  sourceForm,
  onOpenChange,
  onCreated,
}: {
  sourceForm: MetaLeadForm | null
  onOpenChange: (open: boolean) => void
  onCreated: (result: MetaAdsFormCopyResult) => void
}) {
  const mutation = useCreateMetaAdsFormCopyMutation()
  const [error, setError] = useState<string | null>(null)
  const initialValues = useMemo(
    () => ({ name: getDefaultCopyName(sourceForm?.name ?? "Instant Form") }),
    [sourceForm?.name],
  )
  const formik = useFormik<FormCopyValues>({
    initialValues,
    enableReinitialize: true,
    validate: validateCopy,
    onSubmit: async values => {
      if (!sourceForm) return
      setError(null)
      try {
        const result = await mutation.mutateAsync({
          sourceFormId: sourceForm.id,
          name: values.name.trim(),
        })
        onCreated(result)
        handleOpenChange(false)
      } catch (submitError: unknown) {
        setError(getErrorMessage(submitError))
      }
    },
  })

  function handleOpenChange(open: boolean) {
    if (!open) {
      setError(null)
      formik.resetForm()
    }
    onOpenChange(open)
  }

  return (
    <Dialog open={Boolean(sourceForm)} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create an Unrestricted Copy</DialogTitle>
          <DialogDescription>
            Your current form and live ad will stay unchanged.
          </DialogDescription>
        </DialogHeader>
        <FormikProvider value={formik}>
          <form className="space-y-4" onSubmit={formik.handleSubmit}>
            <p className="text-sm text-muted-foreground">
              Versafy will create a second form that works with previews, Meta&apos;s testing
              tool, and shared links. When you are ready, choose the new copy on your ad in
              Ads Manager.
            </p>
            {error ? (
              <Alert variant="destructive" role="alert">
                <AlertTitle>The form could not be copied</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}
            <FormInput
              name="name"
              label="New Instant Form name"
              description="Use a name that is easy to recognize when editing the live ad."
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? "Creating…" : "Create Unrestricted Copy"}
              </Button>
            </DialogFooter>
          </form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  )
}

function getDefaultCopyName(sourceName: string): string {
  return `${sourceName} — Versafy ready`.slice(0, 255)
}

function validateCopy(values: FormCopyValues): FormikErrors<FormCopyValues> {
  const name = values.name.trim()
  if (!name) return { name: "Enter a name for the copied form." }
  if (name.length > 255) return { name: "Use 255 characters or fewer." }
  return {}
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  return "The Instant Form could not be copied. Try again."
}
